import {
  CartItem,
  CartItemCalculationView,
  CartBundleItemView,
  BundleCartItem,
  GiftCardCartItem,
  BundleGiftCardCartItem,
  giftCardDisplayName,
} from './cart-item.model';
import { computeFeeBreakdown } from './fee-breakdown';
import { BundleServiceProviderItemResponse } from '../../models/gitcard.model';
import {
  ShoppingCartItemResponse,
  ShoppingCartItemType,
} from '../../models/shopping-cart.model';

function computeGiftCardCalculation(
  gc: GiftCardCartItem,
  toCurrencyName: string,
  showCommission: boolean,
): CartItemCalculationView | null {
  const buyRate = gc.exchangeRateToIrr?.buyRate;
  if (!buyRate) return null;

  return {
    ...computeFeeBreakdown({
      requestAmount: gc.amount,
      quantity: gc.quantity,
      buyRate,
      commission: gc.commission,
      discount: gc.discount,
      fromCurrencyName: gc.currency?.name ?? '',
      toCurrencyName,
    }),
    serviceProviderItemId: gc.serviceProviderItemId,
    showCommission,
  };
}

function computeBundleGiftCardCalculation(
  g: BundleGiftCardCartItem,
  toCurrencyName: string,
  bundleDiscount: { calculationType: number | null; value: string },
  showCommission: boolean,
): CartItemCalculationView | null {
  const buyRate = g.exchangeRateToIrr?.buyRate;
  if (!buyRate) return null;

  return {
    ...computeFeeBreakdown({
      requestAmount: g.amount,
      quantity: 1,
      buyRate,
      commission: g.commission,
      discount: bundleDiscount,
      fromCurrencyName: g.currency?.name ?? '',
      toCurrencyName,
    }),
    serviceProviderItemId: g.serviceProviderItemId,
    showCommission,
  };
}

function toBundleServiceProviderItem(
  g: BundleGiftCardCartItem,
): BundleServiceProviderItemResponse {
  return {
    serviceProviderItemId: g.serviceProviderItemId,
    providerDisplayName: giftCardDisplayName(g) || null,
    providerLogoUrl: g.providerLogoUrl,
    regionCountryName: null,
    regionCountryLogoUrl: null,
    currencyName: g.currency?.name ?? null,
    currencyDecimalPrecision: g.currency?.decimalPrecision ?? null,
    amount: g.amount,
  };
}

function toCartItem(
  item: ShoppingCartItemResponse,
  rialCurrencyName: string,
  showCommission: boolean,
): CartItem {
  if (item.itemType === ShoppingCartItemType.GiftCard && item.giftCard) {
    const gc = item.giftCard;
    const giftCard: GiftCardCartItem = {
      serviceProviderItemId: gc.serviceProviderItemId,
      providerEnglishName: gc.providerEnglishName,
      providerLogoUrl: gc.providerLogoUrl,
      serviceName: gc.serviceName,
      currency: gc.currency,
      amount: gc.amount,
      quantity: gc.quantity,
      exchangeRateToIrr: gc.exchangeRateToIrr,
      commission: gc.commission,
      discount: gc.discount,
    };
    return {
      shoppingCartItemId: item.shoppingCartItemId,
      itemType: item.itemType,
      giftCard,
      bundle: null,
      calculation: computeGiftCardCalculation(
        giftCard,
        rialCurrencyName,
        showCommission,
      ),
      bundleItems: null,
    };
  }

  if (item.itemType === ShoppingCartItemType.Bundle && item.bundle) {
    const b = item.bundle;
    const giftCards: BundleGiftCardCartItem[] = (b.giftCards ?? []).map(
      (g) => ({
        serviceProviderItemId: g.serviceProviderItemId,
        providerEnglishName: g.providerEnglishName,
        providerLogoUrl: g.providerLogoUrl,
        serviceName: g.serviceName,
        currency: g.currency,
        amount: g.amount,
        exchangeRateToIrr: g.exchangeRateToIrr,
        commission: g.commission,
      }),
    );
    const bundle: BundleCartItem = {
      bundleId: b.bundleId,
      bundleName: b.bundleName,
      logoUrl: b.logoUrl,
      serviceNames: b.serviceNames,
      giftCardCount: b.giftCardCount,
      quantity: b.quantity,
      discount: b.discount,
      giftCards,
    };
    const bundleItems: CartBundleItemView[] = giftCards.map((g) => ({
      item: toBundleServiceProviderItem(g),
      calculation: computeBundleGiftCardCalculation(
        g,
        rialCurrencyName,
        b.discount,
        showCommission,
      ),
      showCommission,
    }));
    return {
      shoppingCartItemId: item.shoppingCartItemId,
      itemType: item.itemType,
      giftCard: null,
      bundle,
      calculation: null,
      bundleItems,
    };
  }

  return {
    shoppingCartItemId: item.shoppingCartItemId,
    itemType: item.itemType,
    giftCard: null,
    bundle: null,
    calculation: null,
    bundleItems: null,
  };
}

export { toCartItem, computeFeeBreakdown };
